/**
 * ORGANİZASYON — departman bazlı günlük puantaj takibi.
 *
 * Kural (routes/organizasyon.py): bir personelin günlük puantajını DEPARTMANININ
 * SORUMLUSU doldurur; sorumlu kendisininkini de. Sorumlu atanmamış, görevde
 * değil ya da o gün izinliyse devir yok — genel puantaj yetkilisi doldurur.
 *
 * Sorumlu ataması burada DEĞİL, Admin Paneli → Departman ve Pozisyonlar'da:
 * departmanın tanım kaydı orada yönetiliyor, aynı alanı iki ekrandan
 * düzenletmek iki ayrı gerçek üretirdi.
 *
 * Amir ağacı (`employees.amir_id`) 2026-09-18'de bu işten çekildi; şema sekmesi
 * de kaldırıldı. Geçmişi git'te.
 *
 * Link yönetimi (oluştur / yenile / iptal) ve token'ın görünmesi yalnızca
 * `timesheet/approve` yetkisinde: token puantaja yazma anahtarı.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, RefreshCw, Ban, Link2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppGonder from '@/components/WhatsAppGonder';

const bugunTr = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);

const DURUM = {
  girilmedi: { etiket: 'Girilmedi', sinif: 'bg-[#a34b3f]/10 text-[#a34b3f]' },
  eksik: { etiket: 'Eksik', sinif: 'bg-[#7a6a2e]/10 text-[#7a6a2e]' },
  tamam: { etiket: 'Tamam', sinif: 'bg-[#3d6e4e]/10 text-[#3d6e4e]' },
  sorumlu_izinde: { etiket: 'Sorumlu izinli — yetkili girecek', sinif: 'bg-[#2e6fa0]/10 text-[#2e6fa0]' },
  sorumlu_gorevde_degil: { etiket: 'Sorumlu görevde değil — yetkili girecek', sinif: 'bg-[#8a7e70]/15 text-[#5c554b]' },
  sorumlu_yok: { etiket: 'Sorumlu atanmamış', sinif: 'bg-[#a34b3f]/10 text-[#a34b3f]' },
};

const linkAdresi = (token) => `${window.location.origin}/puantaj/${token}`;

export default function Organizasyon() {
  usePageTitle('Organizasyon');
  const { hasPermission } = useAuth();
  const linkYetkisi = hasPermission('timesheet', 'approve');
  const [tarih, setTarih] = useState(bugunTr());
  const [veri, setVeri] = useState(null);
  const [mesgul, setMesgul] = useState(null);

  const yukle = useCallback(() => {
    api.get('/organizasyon/gunluk-takip', { params: { tarih } })
      .then(r => setVeri(r.data)).catch(() => toast.error('Günlük takip okunamadı'));
  }, [tarih]);
  useEffect(() => { yukle(); }, [yukle]);

  const kopyala = async (token) => {
    try {
      await navigator.clipboard.writeText(linkAdresi(token));
      toast.success('Bağlantı kopyalandı');
    } catch {
      // http üzerinde clipboard API yok; elle kopyalanabilsin
      window.prompt('Bağlantıyı kopyalayın:', linkAdresi(token));
    }
  };

  const linkIslem = async (d, tur) => {
    if (tur === 'yenile' && d.link_var
        && !window.confirm(`${d.sorumlu_adi} için yeni bağlantı üretilsin mi? Eski bağlantı çalışmayı bırakır.`)) return;
    if (tur === 'iptal' && !window.confirm(`${d.sorumlu_adi} için bağlantı iptal edilsin mi?`)) return;
    setMesgul(d.departman_id);
    try {
      if (tur === 'iptal') {
        await api.delete(`/organizasyon/${d.sorumlu_id}/puantaj-linki`);
        toast.success('Bağlantı iptal edildi');
      } else {
        const r = await api.post(`/organizasyon/${d.sorumlu_id}/puantaj-linki`);
        await kopyala(r.data.token);
      }
      yukle();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'İşlem başarısız');
    } finally { setMesgul(null); }
  };

  const sorumsuz = (veri?.departmanlar || []).filter(d => d.durum === 'sorumlu_yok');

  return (
    <div data-testid="organizasyon-sayfasi">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Organizasyon</h1>
        <p className="text-sm text-[#8a7e70]">
          Günlük puantajı her departmanın sorumlusu doldurur. Tahakkuk yalnızca puantaj / tahakkuk yetkilisindedir.
        </p>
      </div>

      {sorumsuz.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-[#7a6a2e]/30 bg-[#7a6a2e]/10 px-3 py-2">
          <AlertTriangle size={15} className="text-[#7a6a2e] mt-0.5 shrink-0" />
          <p className="text-xs text-[#5c554b]">
            <b>{sorumsuz.map(d => d.departman).join(', ')}</b> için sorumlu atanmamış; bu departmanların
            puantajını yalnızca genel puantaj yetkilisi girebilir.{' '}
            <Link to="/erp/admin#departmanlar" className="text-[#3d6e4e] hover:underline">
              Admin Paneli → Departman ve Pozisyonlar
            </Link>{' '}ekranından atayabilirsiniz.
          </p>
        </div>
      )}

      {!veri ? <p className="text-sm text-[#8a7e70]">Yükleniyor…</p> : (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-0">
            <div className="flex items-center gap-3 p-3 border-b border-[#d0c4b0] flex-wrap">
              <Input type="date" value={tarih} max={bugunTr()} onChange={(ev) => setTarih(ev.target.value || bugunTr())}
                className="w-44 h-9 bg-white border-[#d0c4b0]" data-testid="takip-tarih" />
              {Object.keys(DURUM).map(d => veri.ozet[d] > 0 && (
                <span key={d} className={`status-badge ${DURUM[d].sinif}`}>{DURUM[d].etiket}: {veri.ozet[d]}</span>
              ))}
              {veri.ay_onayli && <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Ay tahakkuk edildi</span>}
              <Link to="/erp/hr/timesheet" className="ml-auto text-xs text-[#3d6e4e] hover:underline">
                Puantaj → Günlük Giriş
              </Link>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    {['Departman', 'Sorumlu', 'Personel', 'Girilen', 'Durum', 'Son link girişi', 'Bağlantı'].map((b, i) => (
                      <TableHead key={b} className={`text-[#1a1916] font-bold text-xs uppercase ${i === 6 ? 'text-right' : ''}`}>{b}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {veri.departmanlar.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-[#8a7e70]">
                      Bu gün kadroda personel yok.
                    </TableCell></TableRow>
                  )}
                  {veri.departmanlar.map(d => {
                    const dur = DURUM[d.durum];
                    return (
                      <TableRow key={d.departman_id || 'departmansiz'} className="border-[#d0c4b0]"
                        data-testid={`takip-${d.departman_id || 'departmansiz'}`}>
                        <TableCell className="text-sm font-medium text-[#1a1916]">{d.departman}</TableCell>
                        <TableCell className="text-sm text-[#3F3E39]">{d.sorumlu_adi || '—'}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{d.personel_sayisi}</TableCell>
                        <TableCell className="text-sm font-mono" title={d.girilmeyenler.length ? `Girilmeyen: ${d.girilmeyenler.join(', ')}` : ''}>
                          {d.girilen}/{d.personel_sayisi}
                          {d.girilmeyenler.length > 0 && d.girilen > 0 && (
                            <p className="text-[10px] font-sans text-[#8a7e70] truncate max-w-[220px]">{d.girilmeyenler.join(', ')}</p>
                          )}
                        </TableCell>
                        <TableCell><span className={`status-badge ${dur.sinif}`}>{dur.etiket}</span></TableCell>
                        <TableCell className="text-xs text-[#3F3E39] whitespace-nowrap">
                          {d.son_link_girisi ? new Date(d.son_link_girisi).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—'}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {!d.sorumlu_id || d.durum === 'sorumlu_gorevde_degil' ? (
                            <span className="text-xs text-[#8a7e70]">—</span>
                          ) : d.sorumlu_tur === 'kullanici' ? (
                            /* Kullanıcı sorumlunun girişi var; girişsiz bağlantı verilmiyor */
                            <span className="text-xs text-[#8a7e70]">Uygulamadan girer</span>
                          ) : !linkYetkisi ? (
                            <span className="text-xs text-[#8a7e70]">{d.link_var ? 'Bağlantı var' : 'Bağlantı yok'}</span>
                          ) : d.link_token ? (
                            <>
                              <Button size="sm" variant="ghost" title="Bağlantıyı kopyala" onClick={() => kopyala(d.link_token)}><Copy size={14} /></Button>
                              <WhatsAppGonder modul="puantaj" kayitId={d.sorumlu_id} etiket="WhatsApp"
                                variant="ghost" className="text-xs h-8 px-2 border-0" testId={`wa-puantaj-${d.departman_id}`} />
                              <Button size="sm" variant="ghost" title="Yeni bağlantı üret" disabled={mesgul === d.departman_id}
                                onClick={() => linkIslem(d, 'yenile')}><RefreshCw size={14} /></Button>
                              <Button size="sm" variant="ghost" title="Bağlantıyı iptal et" disabled={mesgul === d.departman_id}
                                onClick={() => linkIslem(d, 'iptal')}><Ban size={14} className="text-[#a34b3f]" /></Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8 border-[#d0c4b0]" disabled={mesgul === d.departman_id}
                              onClick={() => linkIslem(d, 'yenile')} data-testid={`link-olustur-${d.departman_id}`}>
                              <Link2 size={13} className="mr-1" /> Bağlantı oluştur
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="px-3 py-2 text-[11px] text-[#8a7e70] border-t border-[#d0c4b0]">
              "Girildi" sayılması için departmandaki herkese o gün açıkça bir durum işlenmiş olmalı. Onaylı izin günleri de sayılır.
              Bağlantıdan bugün ve önceki 2 gün girilebilir; izinler bağlantıdan işaretlenemez.
              Bir kişi birden çok departmanın sorumlusuysa hepsini TEK bağlantıdan doldurur.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
